/**
 * FirstRunHero — "Meet your AI Data Analyst" hero card
 *
 * Shown on the Home screen when the user has no data sources, models, or dashboards.
 * Premium enterprise styling with Fluent tokens for dark mode support.
 */

import React from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  PlugConnected20Regular,
  BrainCircuit20Regular,
  Board20Regular,
  ChatSparkle20Regular,
  ArrowUpload20Regular,
} from '@fluentui/react-icons';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1060px',
    margin: '0 auto',
    width: '100%',
    gap: '32px',
  },
  heroCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    ...shorthands.padding('48px', '40px', '40px'),
    ...shorthands.borderRadius('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    backgroundImage: `linear-gradient(180deg, ${tokens.colorNeutralBackground2} 0%, ${tokens.colorNeutralBackground3} 100%)`,
    boxShadow: tokens.shadow4,
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: '36px',
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '22px',
    color: tokens.colorNeutralForeground2,
    maxWidth: '600px',
    marginBottom: '28px',
  },
  ctaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  // ─── Four-step flow ──────────────────────────────────────────────────
  stepsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '32px',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderRadius('10px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('6px', '12px'),
  },
  stepIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  stepLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
  },
  stepDivider: {
    width: '16px',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke3,
    flexShrink: 0,
  },
});

// ─── Props ───────────────────────────────────────────────────────────────────

interface FirstRunHeroProps {
  onConnectSource: () => void;
  onImportFile: () => void;
}

// ─── Steps data ──────────────────────────────────────────────────────────────

const steps = [
  { icon: <PlugConnected20Regular />, label: 'Connect data' },
  { icon: <BrainCircuit20Regular />, label: 'AI understands relationships' },
  { icon: <Board20Regular />, label: 'Dashboards appear' },
  { icon: <ChatSparkle20Regular />, label: 'Ask business questions' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const FirstRunHero: React.FC<FirstRunHeroProps> = ({ onConnectSource, onImportFile }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.heroCard}>
        <Text className={styles.title}>Meet your AI Data Analyst</Text>
        <Text className={styles.subtitle}>
          Connect your data once. AI will understand it, build the semantic model,
          discover insights, and create dashboards automatically.
        </Text>

        <div className={styles.ctaRow}>
          <Button
            appearance="primary"
            size="large"
            icon={<PlugConnected20Regular />}
            onClick={onConnectSource}
          >
            Connect your first data source
          </Button>
          <Button
            appearance="outline"
            size="large"
            icon={<ArrowUpload20Regular />}
            onClick={onImportFile}
          >
            Import Excel or CSV
          </Button>
        </div>

        {/* Four-step flow */}
        <div className={styles.stepsRow}>
          {steps.map((step, idx) => (
            <React.Fragment key={step.label}>
              {idx > 0 && <div className={styles.stepDivider} />}
              <div className={styles.step}>
                <span className={styles.stepIcon}>{step.icon}</span>
                <Text className={styles.stepLabel}>{step.label}</Text>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FirstRunHero;